/**
 * ReadArtifactConfigRenderer — config for read_today_artifact node.
 *
 * Selects artifact_type + target_date_field, then shows the data_schema
 * fields (read-only) so the user knows what outputs this node will produce.
 * Symmetric with ArtifactConfigRenderer's batch mode display.
 */

import React, { useMemo } from 'react';
import { useArtifactTypes } from '@/features/system-settings/hooks/useSystemConfig';
import { useI18n } from '@/i18n';
import type { BaseCustomRendererProps } from './types';
import type { ArtifactFieldDef } from '@/types/system';

export const ReadArtifactConfigRenderer: React.FC<BaseCustomRendererProps> = ({
  nodeData,
  isDisabled = false,
  onBatchUpdate,
}) => {
  const { data: artifactTypes = [], isLoading } = useArtifactTypes();
  const { language } = useI18n();

  const selectedType = (nodeData.artifact_type as string) || '';
  const selectedConfig = useMemo(
    () => artifactTypes.find(t => t.id === selectedType)?.config,
    [artifactTypes, selectedType],
  );
  const dataSchemaFields = useMemo(
    () => (selectedConfig?.data_schema || []) as ArtifactFieldDef[],
    [selectedConfig],
  );

  const handleUpdate = (field: string, value: unknown) => {
    onBatchUpdate?.({ [field]: value });
  };

  const getFieldLabel = (f: ArtifactFieldDef) =>
    language === 'zh' && f.label_zh ? f.label_zh : f.label || f.key;

  return (
    <div className="space-y-4">
      {/* Artifact Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          读取产物类型
          {isLoading && <span className="ml-2 text-gray-400 text-xs">加载中...</span>}
        </label>
        <select
          value={selectedType}
          onChange={(e) => handleUpdate('artifact_type', e.target.value)}
          disabled={isLoading || isDisabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">选择产物类型...</option>
          {artifactTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {language === 'zh' && type.config.name_zh ? type.config.name_zh : type.config.name} ({type.id})
            </option>
          ))}
        </select>
      </div>

      {/* Target Date */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">目标日期</label>
        <select
          value={(nodeData.target_date_field as string) || 'today_in_user_tz'}
          onChange={(e) => handleUpdate('target_date_field', e.target.value)}
          disabled={isDisabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="today_in_user_tz">今天（用户时区）</option>
          <option value="tomorrow_in_user_tz">明天（用户时区）</option>
        </select>
      </div>

      {/* Data Schema Fields — read-only, shows what this node outputs */}
      {selectedType && dataSchemaFields.length > 0 && (
        <div className="pl-3 border-l-2 border-blue-200">
          <div className="text-xs font-medium text-blue-800 mb-1.5">
            输出字段（由 artifact_type 的 data_schema 定义）
          </div>
          {dataSchemaFields.map(f => (
            <div key={f.key} className="flex items-center gap-2 text-xs px-2 py-1 bg-white rounded border border-blue-100 mb-1">
              <code className="font-mono text-blue-700">{f.key}</code>
              <span className="text-gray-400">·</span>
              <span className="text-gray-500">{getFieldLabel(f)}</span>
              {f.required && <span className="text-red-400 text-[10px]">*</span>}
            </div>
          ))}
          <p className="text-[10px] text-gray-400 pt-1">
            读取 artifact → 按 data_schema 展开为输出字段 → 下游通过 input_mapping 消费
          </p>
        </div>
      )}
    </div>
  );
};
